import type {
  AgentSession,
  AlertInstance,
  AlertRule,
  AuditEvent,
  Connection,
  Container,
  CorpusArtifact,
  CorpusBinding,
  GovernanceFinding,
  GovernancePolicy,
  Deployment,
  DeploymentPlacementOutcome,
  Domain,
  Environment,
  Node,
  ImportProposal,
  Instance,
  LlmCredentialSummary,
  Placement,
  Project,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  ServiceHealthcheck,
  ServiceRestartPolicy,
  ServiceVolume,
  User,
} from "./types";

const NOW = Date.now();
const hoursAgo = (h: number) => new Date(NOW - h * 60 * 60 * 1000).toISOString();
const daysAgo = (d: number) => hoursAgo(d * 24);
const minutesAgo = (m: number) => new Date(NOW - m * 60 * 1000).toISOString();

// First-class environments (ADR-0035 §1). A believable promotion chain:
// development -> staging -> production, production protected and requiring
// promotion, with a node-type restriction. `tenant` is the owning tenant.
export const ENVIRONMENTS: Environment[] = [
  {
    id: "env-development",
    name: "development",
    class: "development",
    requiresPromotion: false,
    allowedNodeTypes: [],
    protected: false,
    tenant: "fleet-prod",
    createdAt: daysAgo(90),
    updatedAt: daysAgo(90),
  },
  {
    id: "env-staging",
    name: "staging",
    class: "staging",
    requiresPromotion: false,
    allowedNodeTypes: [],
    protected: false,
    sourceEnvId: "env-development",
    tenant: "fleet-prod",
    createdAt: daysAgo(90),
    updatedAt: daysAgo(30),
  },
  {
    id: "env-production",
    name: "production",
    class: "production",
    requiresPromotion: true,
    allowedNodeTypes: ["standard"],
    protected: true,
    sourceEnvId: "env-staging",
    tenant: "fleet-prod",
    createdAt: daysAgo(90),
    updatedAt: daysAgo(2),
  },
];

export const NODES: Node[] = [
  {
    id: "node-prod-web-1",
    name: "prod-web-1",
    hostname: "web-1.prod.internal",
    status: "online",
    agentVersion: "0.4.2",
    tenant: "fleet-prod",
    containerCount: 4,
    lastSeenAt: minutesAgo(1),
    tags: ["role:web", "region:us-east"],
    // Operator has already set an override (ADR-0029 §5) — exercises the
    // "editing an existing value" path, not just the empty/unset one.
    connectAddress: "10.10.0.11:4433",
    environmentId: "env-production",
    environmentName: "Production",
    nodeType: "standard",
  },
  {
    id: "node-prod-db-1",
    name: "prod-db-1",
    hostname: "db-1.prod.internal",
    status: "online",
    agentVersion: "0.4.2",
    tenant: "fleet-prod",
    containerCount: 3,
    lastSeenAt: minutesAgo(2),
    tags: ["role:observability", "region:us-east"],
    connectAddress: "",
    environmentId: "env-production",
    environmentName: "Production",
    nodeType: "standard",
  },
  {
    id: "node-prod-edge-1",
    name: "prod-edge-1",
    hostname: "edge-1.prod.internal",
    status: "offline",
    agentVersion: "0.3.9",
    tenant: "fleet-prod",
    containerCount: 1,
    lastSeenAt: hoursAgo(6),
    tags: ["role:edge", "region:eu-west"],
    connectAddress: "",
    environmentId: "env-production",
    environmentName: "Production",
    nodeType: "standard",
  },
  {
    id: "node-staging-1",
    name: "staging-1",
    hostname: "staging-1.internal",
    status: "online",
    agentVersion: "0.4.2",
    tenant: "fleet-staging",
    containerCount: 2,
    lastSeenAt: minutesAgo(4),
    tags: ["role:web"],
    connectAddress: "",
    environmentId: "env-staging",
    environmentName: "Staging",
    nodeType: "standard",
  },
  {
    // A developer's own machine (ADR-0034): a `local` node in this tenant, not
    // attached to a managed environment — exercises the node-type badge and the
    // empty-environment rendering in one row. A production-class environment can
    // never hold a local node, which is exactly why it carries no environment.
    id: "node-homelab-nuc",
    name: "homelab-nuc",
    hostname: "nuc.lan",
    status: "unknown",
    agentVersion: null,
    tenant: "fleet-prod",
    containerCount: 0,
    lastSeenAt: null,
    tags: [],
    connectAddress: "",
    environmentId: "",
    environmentName: "",
    nodeType: "local",
  },
];

export const CONTAINERS: Container[] = [
  {
    id: "c-storefront-web",
    name: "storefront-web",
    image: "nginx:1.27",
    state: "running",
    status: "Up 3 days",
    createdAt: daysAgo(5),
    startedAt: daysAgo(3),
    tenant: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    ports: [{ containerPort: 80, protocol: "tcp", hostPort: 8080 }],
    labels: { "com.comebye.service": "web" },
    managed: true,
    // #288: a managed container maps to its owning service, so the detail view can jump
    // to it (matching PLACEMENTS/SERVICES above).
    service: {
      serviceId: "svc-storefront-web",
      serviceName: "web",
      projectId: "proj-storefront",
      projectName: "Storefront",
      placementId: "plc-storefront-web-1",
    },
  },
  {
    id: "c-storefront-api",
    name: "storefront-api",
    image: "ghcr.io/acme/api:2.4.1",
    state: "running",
    status: "Up 3 days",
    createdAt: daysAgo(5),
    startedAt: daysAgo(3),
    tenant: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3000 }],
    labels: { "com.comebye.service": "api" },
    managed: true,
    service: {
      serviceId: "svc-storefront-api",
      serviceName: "api",
      projectId: "proj-storefront",
      projectName: "Storefront",
      placementId: "plc-storefront-api-1",
    },
  },
  {
    id: "c-storefront-worker",
    name: "storefront-worker",
    image: "ghcr.io/acme/worker:2.4.1",
    state: "restarting",
    status: "Restarting (1) 12 seconds ago",
    createdAt: daysAgo(5),
    startedAt: minutesAgo(1),
    tenant: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    ports: [],
    labels: { "com.comebye.service": "worker" },
    managed: true,
  },
  {
    id: "c-adhoc-debug-shell",
    name: "adhoc-debug-shell",
    image: "alpine:3.20",
    state: "exited",
    status: "Exited (0) 4 hours ago",
    createdAt: hoursAgo(4),
    startedAt: hoursAgo(4),
    tenant: "fleet-prod",
    nodeId: "node-prod-web-1",
    nodeName: "prod-web-1",
    ports: [],
    labels: {},
    // No `com.comebye.service`/placement label — a manually `docker run` debug
    // shell, exactly the ADR-0038 "brownfield" adoptable case.
    managed: false,
  },
  {
    id: "c-observability-prometheus",
    name: "observability-prometheus",
    image: "prom/prometheus:v2.53.0",
    state: "running",
    status: "Up 2 days",
    createdAt: daysAgo(2),
    startedAt: daysAgo(2),
    tenant: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    ports: [{ containerPort: 9090, protocol: "tcp", hostPort: 9090 }],
    labels: { "com.comebye.service": "prometheus" },
    managed: true,
  },
  {
    id: "c-observability-grafana",
    name: "observability-grafana",
    image: "grafana/grafana:11.1.0",
    state: "paused",
    status: "Up 2 days (Paused)",
    createdAt: daysAgo(2),
    startedAt: daysAgo(2),
    tenant: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3001 }],
    labels: { "com.comebye.service": "grafana" },
    managed: true,
  },
  {
    id: "c-observability-loki",
    name: "observability-loki",
    image: "grafana/loki:3.1.0",
    state: "exited",
    status: "Exited (137) 2 hours ago",
    createdAt: daysAgo(2),
    startedAt: hoursAgo(2),
    tenant: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    ports: [],
    labels: { "com.comebye.service": "loki" },
    managed: true,
  },
  {
    id: "c-edge-traefik",
    name: "edge-traefik",
    image: "traefik:v3.1",
    state: "dead",
    status: "Dead — node unreachable",
    createdAt: daysAgo(10),
    startedAt: null,
    tenant: "fleet-prod",
    nodeId: "node-prod-edge-1",
    nodeName: "prod-edge-1",
    ports: [{ containerPort: 443, protocol: "tcp", hostPort: 443 }],
    labels: { "com.comebye.service": "traefik" },
    managed: true,
  },
  {
    id: "c-staging-web",
    name: "staging-web",
    image: "nginx:1.27",
    state: "running",
    status: "Up 5 hours",
    createdAt: hoursAgo(5),
    startedAt: hoursAgo(5),
    tenant: "fleet-staging",
    nodeId: "node-staging-1",
    nodeName: "staging-1",
    ports: [{ containerPort: 80, protocol: "tcp", hostPort: 8080 }],
    labels: { "com.comebye.service": "web" },
    managed: true,
  },
  {
    id: "c-staging-api",
    name: "staging-api",
    image: "ghcr.io/acme/api:2.5.0-rc1",
    state: "running",
    status: "Up 5 hours",
    createdAt: hoursAgo(5),
    startedAt: hoursAgo(5),
    tenant: "fleet-staging",
    nodeId: "node-staging-1",
    nodeName: "staging-1",
    ports: [{ containerPort: 3000, protocol: "tcp", hostPort: 3000 }],
    labels: { "com.comebye.service": "api" },
    managed: true,
  },
  // ---- Unmanaged (adoptable) containers — ADR-0038 the Migrator's brownfield case ----
  // Never Comebye-deployed: no placement label, `managed: false`. Their ADOPT_SPECS
  // entries below (deliberately) embed secret-shaped values in `command`/`healthcheck` —
  // the §4 masking demo needs at least one adoptable container that actually exercises
  // the "these may contain secrets" warning, not a sanitized strawman.
  {
    id: "c-legacy-redis",
    name: "legacy-redis",
    image: "redis:7-alpine",
    state: "running",
    status: "Up 41 days",
    createdAt: daysAgo(41),
    startedAt: daysAgo(41),
    tenant: "fleet-prod",
    nodeId: "node-prod-db-1",
    nodeName: "prod-db-1",
    ports: [{ containerPort: 6379, protocol: "tcp", hostPort: 6379 }],
    // A brownfield container predates Comebye — Portainer-style stack labels, not ours.
    labels: { "com.docker.compose.project": "legacy-cache" },
    managed: false,
  },
  {
    id: "c-legacy-registry",
    name: "legacy-registry",
    image: "registry:2",
    state: "running",
    status: "Up 96 days",
    createdAt: daysAgo(96),
    startedAt: daysAgo(96),
    tenant: "fleet-prod",
    nodeId: "node-prod-edge-1",
    nodeName: "prod-edge-1",
    ports: [{ containerPort: 5000, protocol: "tcp", hostPort: 5000 }],
    labels: {},
    managed: false,
  },
];

/**
 * `AdoptSpec`-shaped capture data the mock's `adoptService` hands back per unmanaged
 * container id (see `ApiClient.adoptService`'s doc comment) — stands in for what a real
 * Docker inspect would surface. Two of the three unmanaged fixtures above deliberately
 * embed a secret-shaped value in `command`/`healthcheck` (a `--requirepass` flag, a
 * Bearer token in a curl probe) — exactly the ADR-0038 §4 case the review UI's masking
 * exists for, not a sanitized strawman. `envKeys` mirrors the real zero-egress firewall:
 * names only, `adoptService` fills in blank values, never a value from here.
 */
export interface MockAdoptSpec {
  volumes: ServiceVolume[];
  command: string[];
  entrypoint: string[];
  restartPolicy: ServiceRestartPolicy;
  healthcheck: ServiceHealthcheck;
  envKeys: string[];
}

/** Nothing meaningfully captured — the fallback for an unmanaged container with no
 * specific entry in `ADOPT_SPECS` below (e.g. a plain debug shell). */
export const EMPTY_ADOPT_SPEC: MockAdoptSpec = {
  volumes: [],
  command: [],
  entrypoint: [],
  restartPolicy: { name: "" },
  healthcheck: {},
  envKeys: [],
};

export const ADOPT_SPECS: Record<string, MockAdoptSpec> = {
  "c-adhoc-debug-shell": {
    volumes: [],
    command: ["sh"],
    entrypoint: [],
    restartPolicy: { name: "no" },
    healthcheck: {},
    envKeys: ["TERM"],
  },
  "c-legacy-redis": {
    volumes: [{ type: "volume", source: "legacy-redis-data", target: "/data", mode: "" }],
    // Secret-shaped on purpose (ADR-0038 §4's exact motivating example) — this is what
    // the masked-by-default review step exists for.
    command: ["redis-server", "--requirepass", "hunter2-prod-cache", "--appendonly", "yes"],
    entrypoint: [],
    restartPolicy: { name: "unless-stopped" },
    healthcheck: {
      test: ["CMD", "redis-cli", "-a", "hunter2-prod-cache", "ping"],
      intervalSeconds: 30,
      timeoutSeconds: 5,
      retries: 3,
    },
    envKeys: ["REDIS_REPLICATION_MODE"],
  },
  "c-legacy-registry": {
    volumes: [{ type: "volume", source: "legacy-registry-data", target: "/var/lib/registry", mode: "" }],
    command: [],
    entrypoint: ["/entrypoint.sh", "/etc/docker/registry/config.yml"],
    restartPolicy: { name: "always" },
    healthcheck: {
      test: [
        "CMD-SHELL",
        "curl -sf -H 'Authorization: Bearer legacy-registry-static-token-9f21' http://localhost:5000/v2/ || exit 1",
      ],
      intervalSeconds: 15,
      timeoutSeconds: 5,
      retries: 3,
    },
    envKeys: ["REGISTRY_STORAGE_DELETE_ENABLED"],
  },
};

export const SECRETS: SecretSummary[] = [
  {
    id: "secret-dockerhub-pull",
    name: "dockerhub-pull",
    version: 1,
    tenant: "fleet-prod",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
  {
    id: "secret-github-deploy-key",
    name: "github-deploy-key",
    version: 3,
    tenant: "fleet-prod",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(12),
  },
  {
    id: "secret-staging-dockerhub-pull",
    name: "dockerhub-pull",
    version: 1,
    tenant: "fleet-staging",
    createdBy: "founder",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
];

/**
 * Environment-wide (not fleet-scoped, like the lists above). `usr-admin`'s
 * id is what `mockClient.ts`'s stub `login()`/`getAccount()` uses as "you" —
 * see the note there — so the "can't remove/deactivate yourself" guard has
 * something real to demo against.
 */
export const USERS: User[] = [
  {
    id: "usr-admin",
    username: "admin",
    displayName: "Founder Admin",
    role: "admin",
    active: true,
    createdAt: daysAgo(120),
    lastLoginAt: minutesAgo(3),
    authMethod: "password",
    status: "active",
  },
  {
    id: "usr-ops",
    username: "ops",
    displayName: "Ops Operator",
    role: "operator",
    active: true,
    createdAt: daysAgo(60),
    lastLoginAt: hoursAgo(5),
    authMethod: "password",
    status: "active",
  },
  {
    id: "usr-viewer",
    username: "viewer",
    displayName: "Read-only Viewer",
    role: "viewer",
    active: true,
    createdAt: daysAgo(45),
    lastLoginAt: daysAgo(1),
    authMethod: "password",
    status: "active",
  },
  {
    id: "usr-ex-contractor",
    username: "ex-contractor",
    displayName: "Former Contractor",
    role: "operator",
    active: false,
    createdAt: daysAgo(200),
    lastLoginAt: daysAgo(40),
    authMethod: "password",
    status: "inactive",
  },
  // An SSO user someone has already signed in through once — ADR-0037 §4's
  // steady state: no password, promoted to active by its first verified link.
  {
    id: "usr-sso-active",
    username: "priya.sso",
    displayName: "Priya (SSO)",
    role: "operator",
    active: true,
    createdAt: daysAgo(10),
    lastLoginAt: hoursAgo(2),
    authMethod: "sso",
    status: "active",
  },
  // An SSO user an admin pre-invited who has NOT signed in yet (ADR-0037 §4's
  // pre-link state) — demonstrates the UsersPage "Invited" status distinct
  // from Active/Inactive, and the hidden Reset-password action (no credential
  // to reset).
  {
    id: "usr-sso-invited",
    username: "sam.invited",
    displayName: "Sam Invited",
    role: "viewer",
    active: false,
    createdAt: daysAgo(2),
    authMethod: "sso",
    status: "invited",
  },
];

/** Action/resource/outcome templates cycled to build a believable, varied
 * synthetic trail — not meant to be exhaustive, just plausible enough to
 * exercise the audit viewer's table, pagination, and outcome styling. */
const AUDIT_TEMPLATES: { action: string; resource: string; outcome: string }[] = [
  { action: "login.success", resource: "session", outcome: "success" },
  { action: "login.failure", resource: "session", outcome: "failure" },
  { action: "secret.set", resource: "secret", outcome: "success" },
  { action: "secret.rotate", resource: "secret", outcome: "success" },
  { action: "secret.delete", resource: "secret", outcome: "success" },
  { action: "service.deploy", resource: "service", outcome: "success" },
  { action: "project.create", resource: "project", outcome: "success" },
  { action: "container.exec", resource: "container", outcome: "success" },
  { action: "container.restart", resource: "container", outcome: "success" },
  { action: "node.enroll", resource: "node", outcome: "success" },
  { action: "docker.deny", resource: "container", outcome: "deny" },
  { action: "user.update", resource: "user", outcome: "success" },
];

const AUDIT_ACTORS: { userId: string; role: string }[] = [
  { userId: "usr-admin", role: "admin" },
  { userId: "usr-ops", role: "operator" },
];

/**
 * Builds a believable, newest-first synthetic trail for one fleet — enough
 * rows (default 130) to make the audit viewer's cursor pagination worth
 * exercising. Deterministic per fleet (no `Math.random`) so the page count
 * and rows a demo sees are stable across reloads.
 */
export function buildAuditEvents(tenant: string, count = 130): AuditEvent[] {
  const events: AuditEvent[] = [];
  for (let i = 0; i < count; i++) {
    const seq = count - i;
    const template = AUDIT_TEMPLATES[i % AUDIT_TEMPLATES.length];
    const actor = AUDIT_ACTORS[i % AUDIT_ACTORS.length];
    events.push({
      id: `aud-mock-${tenant}-${seq}`,
      seq,
      occurredAt: minutesAgo(i * 41 + 3),
      actorUserId: actor.userId,
      actorRole: actor.role,
      action: template.action,
      resource: template.resource,
      target: `${template.resource}-${(seq % 9) + 1}`,
      outcome: template.outcome,
      tenant,
      prevHash: `h${seq - 1}`,
      hash: `h${seq}`,
    });
  }
  return events;
}

/**
 * Governed-promotion decisions (ADR-0058/#562) — believable `governance.decision`
 * audit rows for the Governance Decisions view (`features/governance/`). `detail`
 * mirrors `auditGovernanceDecision`'s EXACT format
 * (server/internal/projectsvc/promote.go) byte-for-byte:
 *
 *   governance {allow|deny} promote from={srcEnvId} to={tgtEnvId} image={ref} commit={sha} rules=[{r1, r2}]
 *
 * so `parseGovernanceDetail` (the same parser the real client uses) decodes these
 * fixtures identically to a real server response — a mock that drifted from that
 * format would defeat the point of testing against it. A deliberate MIX of allow and
 * deny (3 of each below), across both services and both fired predicates
 * (`promote.require.source_class`, `promote.require.max_open_findings`), including one
 * deny with TWO fired rules, so the view's rule/verdict rendering has real variety to
 * exercise, not just a single happy-path row.
 */
const GOVERNANCE_DECISION_TEMPLATES: {
  outcome: "allow" | "deny";
  serviceId: string;
  from: string;
  to: string;
  image: string;
  commit: string;
  rules: string[];
  actor: { userId: string; role: string };
  minutesAgo: number;
}[] = [
  {
    outcome: "allow",
    serviceId: "svc-storefront-web",
    from: "env-staging",
    to: "env-production",
    image: "ghcr.io/acme/storefront-web@sha256:aa11bb22cc33dd44ee55ff6600112233aabbccddeeff00112233aabbccddee",
    commit: "a1b2c3d4e5f60718293a4b5c6d7e8f9012345678",
    rules: [],
    actor: { userId: "usr-admin", role: "admin" },
    minutesAgo: 22,
  },
  {
    outcome: "deny",
    serviceId: "svc-storefront-web",
    from: "env-development",
    to: "env-production",
    image: "ghcr.io/acme/storefront-web@sha256:bb22cc33dd44ee55ff6600112233aabbccddeeff00112233aabbccddeeff11",
    commit: "a1b2c3d4e5f60718293a4b5c6d7e8f9012345678",
    rules: ["promote.require.source_class"],
    actor: { userId: "usr-ops", role: "operator" },
    minutesAgo: 130,
  },
  {
    outcome: "allow",
    serviceId: "svc-storefront-worker",
    from: "env-staging",
    to: "env-production",
    image: "ghcr.io/acme/storefront-worker@sha256:cc33dd44ee55ff6600112233aabbccddeeff00112233aabbccddeeff1122",
    commit: "f6e5d4c3b2a190837261504f3e2d1c0b9a887766",
    rules: [],
    actor: { userId: "usr-admin", role: "admin" },
    minutesAgo: 1450,
  },
  {
    outcome: "deny",
    serviceId: "svc-storefront-worker",
    from: "env-staging",
    to: "env-production",
    image: "ghcr.io/acme/storefront-worker@sha256:dd44ee55ff6600112233aabbccddeeff00112233aabbccddeeff112233",
    commit: "f6e5d4c3b2a190837261504f3e2d1c0b9a887766",
    rules: ["promote.require.max_open_findings"],
    actor: { userId: "usr-ops", role: "operator" },
    minutesAgo: 2910,
  },
  {
    outcome: "deny",
    serviceId: "svc-storefront-web",
    from: "env-staging",
    to: "env-production",
    image: "ghcr.io/acme/storefront-web@sha256:ee55ff6600112233aabbccddeeff00112233aabbccddeeff11223344",
    commit: "9f8e7d6c5b4a392817263544f3e2d1c0b9a88776",
    rules: ["promote.require.source_class", "promote.require.max_open_findings"],
    actor: { userId: "usr-admin", role: "admin" },
    minutesAgo: 7220,
  },
  {
    outcome: "allow",
    serviceId: "svc-storefront-worker",
    from: "env-staging",
    to: "env-production",
    image: "ghcr.io/acme/storefront-worker@sha256:ff6600112233aabbccddeeff00112233aabbccddeeff1122334455",
    commit: "9f8e7d6c5b4a392817263544f3e2d1c0b9a88776",
    rules: [],
    actor: { userId: "usr-admin", role: "admin" },
    minutesAgo: 8640,
  },
];

export function buildGovernanceDecisionEvents(tenant: string): AuditEvent[] {
  return GOVERNANCE_DECISION_TEMPLATES.map((tpl, i) => {
    const seq = GOVERNANCE_DECISION_TEMPLATES.length - i;
    return {
      id: `aud-mock-gov-${tenant}-${seq}`,
      seq,
      occurredAt: minutesAgo(tpl.minutesAgo),
      actorUserId: tpl.actor.userId,
      actorRole: tpl.actor.role,
      action: "governance.decision",
      resource: "environment",
      target: tpl.serviceId,
      outcome: tpl.outcome,
      detail: `governance ${tpl.outcome} promote from=${tpl.from} to=${tpl.to} image=${tpl.image} commit=${tpl.commit} rules=[${tpl.rules.join(", ")}]`,
      tenant,
      prevHash: `gh${seq - 1}`,
      hash: `gh${seq}`,
    };
  });
}

/*
 * ---- Hero flow: projects / services / deployments / provider credentials /
 * instances ---- all scoped to fleet-prod, mirroring the real, merged
 * backend contract (#63 projectsvc, #62 provisioning) exactly. Every
 * `Service.status` below starts `"created"` (mockClient's `deployService`
 * flips it once an operator actually deploys, same as the real server);
 * DEPLOYMENTS starts empty for the same reason — a deploy attempt is a real
 * user action, not a seeded fixture.
 *
 * INSTANCES (Hetzner provisioning, #62) is unrelated to deploy since
 * ADR-0025: `proj-storefront` has one running instance and one still
 * provisioning purely to demo the provisioning flow's own states, not deploy
 * target resolution (deploy now runs on placements exclusively — see
 * PLACEMENTS below).
 *
 * The placement graph is what determines deployability: `web` (desiredCount
 * 2) demonstrates the "exactly one replica" refusal despite having a
 * placement; `worker` (desiredCount 1) has exactly one PENDING placement, so
 * it demonstrates a real, successful first deploy end to end; `postgres` is
 * already deployed (its placement starts "running"). `proj-internal-tools`
 * has no services with placements at all, demonstrating the "place it on a
 * node first" refusal (ADR-0025 §2 / ErrNoPlacements).
 */

export const PROVIDER_CREDENTIALS: ProviderCredentialSummary[] = [
  {
    id: "provcred-hetzner-prod",
    kind: "hetzner",
    label: "Hetzner — prod project",
    tenant: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(30),
  },
];

export const LLM_CREDENTIALS: LlmCredentialSummary[] = [
  {
    id: "llm-anthropic-prod",
    provider: "openrouter",
    label: "OpenRouter — dev builds",
    tenant: "fleet-prod",
    models: ["anthropic/claude-3.5-sonnet", "anthropic/claude-3.5-haiku"],
    budgetMonthlyUsd: 200,
    createdBy: "admin",
    createdAt: daysAgo(14),
    updatedAt: daysAgo(14),
  },
  {
    id: "llm-openai-fallback",
    provider: "openai",
    label: "OpenAI — fallback",
    tenant: "fleet-prod",
    budgetMonthlyUsd: 50,
    createdBy: "admin",
    createdAt: daysAgo(7),
    updatedAt: daysAgo(7),
  },
];

export const AGENT_SESSIONS: AgentSession[] = [
  {
    id: "asn_mock-coder-1",
    tenant: "fleet-prod",
    credentialId: "llm-anthropic-prod",
    nodeId: "node-web-1",
    containerId: "ctr-workbench-abc",
    status: "running",
    image: "ghcr.io/comebye/comebye-agent-task:latest",
    label: "Backend refactor",
    createdBy: "admin",
    createdAt: hoursAgo(3),
    updatedAt: hoursAgo(1),
  },
  {
    id: "asn_mock-coder-2",
    tenant: "fleet-prod",
    credentialId: "llm-openai-fallback",
    status: "stopped",
    image: "ghcr.io/comebye/comebye-agent-task:latest",
    label: "Frontend spike",
    createdBy: "admin",
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
    stoppedAt: daysAgo(1),
  },
  {
    id: "asn_mock-coder-3",
    tenant: "fleet-prod",
    credentialId: "llm-anthropic-prod",
    status: "pending",
    image: "ghcr.io/comebye/comebye-agent-task:0.2.0",
    label: "Migration planning",
    createdBy: "admin",
    createdAt: minutesAgo(10),
    updatedAt: minutesAgo(10),
  },
  // ---- Bonnie (local, worktree-per-session) sessions (ADR-0039 P4/P6) ----
  // Distinguished from the hosted workbench rows above by `local: true` +
  // `branch` — see AgentSession's doc comment in types.ts. The Bonnie
  // dashboard filters to these.
  {
    id: "asn_mock-bonnie-1",
    tenant: "fleet-prod",
    credentialId: "llm-anthropic-prod",
    nodeId: "node-laptop-1",
    containerId: "ctr-bonnie-abc",
    status: "running",
    image: "ghcr.io/comebye/bonnie-agent:latest",
    label: "Add rate limiting",
    createdBy: "admin",
    createdAt: hoursAgo(2),
    updatedAt: minutesAgo(5),
    local: true,
    branch: "bonnie/add-rate-limiting",
    dirty: true,
    changeSummary: "4 files changed, 86 insertions(+), 11 deletions(-)",
    node: "brandon-laptop",
  },
  {
    id: "asn_mock-bonnie-2",
    tenant: "fleet-prod",
    credentialId: "llm-anthropic-prod",
    nodeId: "node-laptop-1",
    containerId: "ctr-bonnie-def",
    status: "running",
    image: "ghcr.io/comebye/bonnie-agent:latest",
    label: "Fix flaky healthcheck test",
    createdBy: "admin",
    createdAt: hoursAgo(6),
    updatedAt: hoursAgo(1),
    local: true,
    branch: "bonnie/fix-flaky-healthcheck",
    dirty: false,
    node: "brandon-laptop",
  },
  {
    id: "asn_mock-bonnie-3",
    tenant: "fleet-prod",
    credentialId: "llm-openai-fallback",
    status: "stopped",
    image: "ghcr.io/comebye/bonnie-agent:latest",
    label: "Docs pass for onboarding",
    createdBy: "admin",
    createdAt: daysAgo(1),
    updatedAt: hoursAgo(20),
    stoppedAt: hoursAgo(20),
    local: true,
    branch: "bonnie/docs-onboarding-pass",
    dirty: true,
    changeSummary: "2 files changed, 40 insertions(+)",
    node: "brandon-laptop",
  },
  // ---- Develop (corpus-authoring) sessions (ADR-0052 M6, IA slice 3) ----
  // A develop session is a `local: true` session that also carries
  // `projectId` — see `AgentSession.projectId`'s doc comment in types.ts.
  // `proj-storefront` is the IA slice 3 ProjectDetailPage Sessions tab's
  // primary fixture: one develop session actually tied to that project, so
  // the tab's client-side `projectId` filter has something real to prove.
  {
    id: "asn_mock-develop-1",
    tenant: "fleet-prod",
    credentialId: "llm-anthropic-prod",
    nodeId: "node-laptop-1",
    containerId: "ctr-develop-abc",
    status: "running",
    image: "",
    label: "First pass at the vision",
    createdBy: "admin",
    createdAt: hoursAgo(4),
    updatedAt: minutesAgo(20),
    local: true,
    branch: "develop/storefront-vision",
    dirty: true,
    changeSummary: "3 files changed, 58 insertions(+), 2 deletions(-)",
    node: "brandon-laptop",
    projectId: "proj-storefront",
  },
];

/**
 * `NODES` above and `INSTANCES` below are also mockClient.ts's ONE source of
 * truth for the unified Host read-model (host/deploy UX review, #526) — the
 * mock computes `Host` rows by joining these two on `Instance.agentId ===
 * Node.id` at request time (mirroring `server/internal/hostsvc`'s real join,
 * PR #526), rather than hand-authoring a third, independently maintained
 * `HOSTS` array that could drift from these (charter A-5). See `computeHosts`
 * in mockClient.ts. Between them these fixtures cover every Host state the UI
 * renders:
 *   - `inst-hetzner-web-1` + `node-prod-web-1` join into a managed, online host.
 *   - `node-homelab-nuc` (no matching instance) is a BYO/local host.
 *   - `node-prod-edge-1` (no matching instance) is an offline host.
 *   - `inst-hetzner-provisioning` (no node yet, `status: "provisioning"`) is
 *     still provisioning.
 *   - `inst-hetzner-enrolling` (no node yet, `status: "running"`) is up at the
 *     provider but its agent hasn't enrolled — "running ≠ enrolled".
 *   - `inst-hetzner-failed` (no node yet, `status: "error"`) is a provision
 *     failure.
 */
export const INSTANCES: Instance[] = [
  {
    id: "inst-hetzner-web-1",
    tenant: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "hetzner-42",
    region: "fsn1",
    serverType: "cx22",
    status: "running",
    publicIpv4: "203.0.113.10",
    agentId: "node-prod-web-1",
    createdBy: "admin",
    createdAt: daysAgo(28),
    updatedAt: daysAgo(28),
  },
  {
    id: "inst-hetzner-provisioning",
    tenant: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "",
    region: "fsn1",
    serverType: "cx22",
    status: "provisioning",
    publicIpv4: "",
    createdBy: "admin",
    createdAt: minutesAgo(3),
    updatedAt: minutesAgo(3),
  },
  {
    // The provider finished building the host (status "running") but no
    // Kelpie agent has enrolled onto it yet (Host state "enrolling" —
    // "running ≠ enrolled"). No `agentId` on purpose: a real instance always
    // reserves one before the provider call, but this fixture's whole point
    // is to exercise the no-node branch of the join.
    id: "inst-hetzner-enrolling",
    tenant: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "hetzner-91",
    region: "ash",
    serverType: "cpx11",
    status: "running",
    publicIpv4: "203.0.113.55",
    createdBy: "admin",
    createdAt: minutesAgo(8),
    updatedAt: minutesAgo(1),
  },
  {
    // A provider request that failed outright (Host state "provision_failed") —
    // there is no machine to enroll an agent onto until this is retried.
    id: "inst-hetzner-failed",
    tenant: "fleet-prod",
    projectId: "proj-storefront",
    providerCredId: "provcred-hetzner-prod",
    providerKind: "hetzner",
    externalId: "",
    region: "hel1",
    serverType: "cx23",
    status: "error",
    publicIpv4: "",
    createdBy: "admin",
    createdAt: hoursAgo(1),
    updatedAt: hoursAgo(1),
  },
];

/**
 * A demo `orgmap.Infer` result for `provcred-hetzner-prod` (ADR-0048 §2, #472) —
 * hand-built rather than derived from `INSTANCES` above, because the real
 * proposal reads raw PROVIDER resources (labels/tags), a different shape than
 * the already-provisioned `Instance` rows Comebye itself created. Deliberately
 * spans every confidence/conflict combination the badges need to render:
 * two round-tripped nodes aggregating into a high-confidence "Storefront"
 * project (already exists — demonstrates the idempotent re-import path), one
 * hand-tagged node aggregating into a medium-confidence NEW "Legacy CRM"
 * project, and three low-confidence, non-committable nodes — one of each
 * conflict kind — so every badge and the deferred/committable split are
 * exercised without a live provider account.
 */
export const IMPORT_PROPOSAL_HETZNER: ImportProposal = {
  tenant: "fleet-prod",
  items: [
    {
      level: "node",
      tenant: "fleet-prod",
      project: "Storefront",
      name: "web-1",
      confidence: "high",
      sourceIds: ["hetzner-42"],
      committable: false,
    },
    {
      level: "node",
      tenant: "fleet-prod",
      project: "Storefront",
      name: "db-2",
      confidence: "high",
      sourceIds: ["hetzner-77"],
      committable: false,
    },
    {
      level: "node",
      tenant: "fleet-prod",
      project: "Legacy CRM",
      name: "legacy-crm-1",
      confidence: "medium",
      sourceIds: ["hetzner-91"],
      committable: false,
    },
    {
      level: "node",
      tenant: "fleet-prod",
      name: "orphan-box",
      confidence: "low",
      conflicts: [{ kind: "untagged", detail: "resource carries no labels or tags; a human must map it" }],
      sourceIds: ["hetzner-12"],
      committable: false,
    },
    {
      level: "node",
      tenant: "fleet-prod",
      name: "ops-script-host",
      confidence: "low",
      conflicts: [
        { kind: "foreign-scheme", detail: "resource carries a non-Comebye label/tag scheme; a human must map it" },
      ],
      sourceIds: ["hetzner-33"],
      committable: false,
    },
    {
      level: "node",
      tenant: "fleet-prod",
      name: "acme-shared-lb",
      confidence: "low",
      conflicts: [
        {
          kind: "foreign-tenant",
          detail: "resource is stamped for another tenant and cannot be imported into this one",
        },
      ],
      sourceIds: ["hetzner-55"],
      committable: false,
    },
    {
      level: "project",
      tenant: "fleet-prod",
      project: "Legacy CRM",
      name: "Legacy CRM",
      confidence: "medium",
      sourceIds: ["hetzner-91"],
      committable: true,
    },
    {
      level: "project",
      tenant: "fleet-prod",
      project: "Storefront",
      name: "Storefront",
      confidence: "high",
      sourceIds: ["hetzner-42", "hetzner-77"],
      committable: true,
    },
  ],
};

export const PROJECTS: Project[] = [
  {
    id: "proj-storefront",
    name: "Storefront",
    tenant: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "proj-internal-tools",
    name: "Internal tools",
    tenant: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(9),
  },
  // A freshly created, never-described project (M2's "develop your app" on-ramp, #245):
  // no corpus binding at all — see CORPUS_BINDINGS below, which deliberately carries no
  // entry for this id. Exercises the honest "not bound yet" empty state/bind CTA
  // (CorpusPage) rather than every project fixture already being a corpus success story.
  {
    id: "proj-loyalty-app",
    name: "Loyalty rewards",
    tenant: "fleet-prod",
    createdBy: "admin",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
];

// ---- Corpus (ADR-0052 read / ADR-0053 §3 binding) ----
// Three scenarios, one per PROJECTS entry above, so the mock exercises every state
// CorpusPage must render honestly (A-10):
//   - proj-storefront:      bound AND synced — the "healthy" case (a real rung badge, real
//                            documents).
//   - proj-internal-tools:  bound but never synced — the binding exists but
//                            CORPUS_ARTIFACTS has no entry, so `sourceCommit` reads "" (the
//                            server's own "nothing applied yet" signal) and the page must
//                            show the pending state, never a fabricated "verified" badge.
//   - proj-loyalty-app:     no binding at all — CORPUS_BINDINGS has no entry, so
//                            getCorpusBinding 404s and the page shows the bind CTA.

const STOREFRONT_CORPUS_COMMIT = "a1b2c3d4e5f67890a1b2c3d4e5f67890a1b2c3d4";

export const CORPUS_ARTIFACTS: Record<string, CorpusArtifact[]> = {
  "proj-storefront": [
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "vision",
      slug: "vision",
      title: "Storefront vision",
      seq: 0,
      bodyMd:
        "Storefront is the checkout experience acme.example customers trust for **speed** " +
        "and *honesty about stock*. We win by never showing a size we can't ship, and by " +
        "keeping the pay-to-confirmation loop under two seconds on a mid-tier phone.\n\n" +
        "Non-goals for now: marketplace/multi-seller support, and a native mobile app — both " +
        "are explicitly out of scope until the single-seller web checkout is boring.",
    },
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "roadmap_item",
      slug: "roadmap-mvp-checkout",
      title: "MVP: one-page checkout",
      seq: 1,
      bodyMd:
        "Collapse cart -> address -> payment into a single scroll, no page reloads.\n\n" +
        "- Guest checkout by default; account creation is an opt-in AFTER payment succeeds\n" +
        "- Apple Pay / Google Pay before card entry, not buried in a menu\n" +
        "- Inventory is re-checked at payment time, not just at add-to-cart",
    },
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "roadmap_item",
      slug: "roadmap-loyalty-integration",
      title: "Loyalty points at checkout",
      seq: 2,
      bodyMd:
        "Once `Loyalty rewards` ships its points ledger, surface a redeemable balance on " +
        "the checkout review step. Depends on the `loyalty-app` project's public API, not " +
        "yet stable — tracked as blocked in the planning board, not in this corpus.",
    },
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "decision",
      slug: "decision-0001-postgres",
      title: "ADR-0001: Postgres for orders, not the document store",
      seq: 1,
      bodyMd:
        "We chose Postgres over the team's default document store for the `orders` table " +
        "because an order's line items and payment state need real transactional " +
        "consistency — a partially-written order is a customer-visible bug, not an " +
        "eventual-consistency inconvenience.\n\n" +
        "Superseded: nothing yet. Revisit if write volume ever demands sharding.",
    },
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "decision",
      slug: "decision-0002-stripe",
      title: "ADR-0002: Stripe for payments",
      seq: 2,
      bodyMd:
        "Stripe over a second processor for launch: PCI scope stays entirely off our " +
        "infrastructure (`Elements` tokenizes card data client-side), and Storefront's " +
        "volume doesn't yet justify a second processor's integration cost.",
    },
    {
      sourceCommit: STOREFRONT_CORPUS_COMMIT,
      kind: "governance_rule",
      slug: "governance-code-review",
      title: "Every change to `orders/` ships with a test",
      seq: 0,
      bodyMd:
        "No exceptions, including \"just a copy change\" — the `orders` service has broken " +
        "on copy changes before (a currency-formatting string that silently rounded totals). " +
        "A second reviewer signs off before merge; the author never self-merges.",
    },
  ],
  // proj-internal-tools intentionally has no entry: bound (see CORPUS_BINDINGS) but never
  // synced — `CorpusList.sourceCommit` reads "" for it (mockClient.getCorpus), matching a
  // real project whose first Selkie handoff hasn't landed yet.
};

// CORPUS_SYNCED_AT is the wall-clock instant each project's applied corpus pointer was last
// synced (the server's `CorpusList.syncedAt`, from `CorpusSync.SyncedAt`). Only a SYNCED
// project has an entry: proj-internal-tools (bound but never synced) and proj-loyalty-app
// (unbound) deliberately have none, so mockClient.getCorpus OMITS `syncedAt` for them exactly
// as the server does — a never-synced project never carries a fabricated/zero time (A-10).
export const CORPUS_SYNCED_AT: Record<string, string> = {
  "proj-storefront": daysAgo(2),
};

export const CORPUS_BINDINGS: Record<string, CorpusBinding> = {
  "proj-storefront": {
    projectId: "proj-storefront",
    repoRef: "acme/storefront",
    allowedRefs: ["refs/heads/main"],
    corpusPath: ".comebye/corpus",
    expectedRung: "ci-attested",
    expectedIssuer: "https://token.actions.githubusercontent.com",
    createdBy: "admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  "proj-internal-tools": {
    projectId: "proj-internal-tools",
    repoRef: "acme/internal-tools",
    allowedRefs: ["refs/heads/main"],
    corpusPath: ".comebye/corpus",
    // repo-watch: the zero-config default rung — bound moments ago, awaiting its first
    // scoped GitHub-App read. See CORPUS_ARTIFACTS's comment: deliberately unsynced.
    expectedRung: "repo-watch",
    expectedIssuer: "",
    createdBy: "admin",
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  // proj-loyalty-app intentionally has no entry: no binding at all yet.
};

// GOVERNANCE_FINDINGS (ADR-0059): governing-agent findings, keyed by projectId. proj-storefront
// carries one OPEN finding (exercises the resolve action + the advisory-vs-enforced surface) and
// one already-RESOLVED finding (exercises the resolved rendering). Other projects have none, so the
// panel's honest empty state is exercised too. In the mock these are pre-seeded objections; in
// production they are appended only by the server-side producer (never the browser).
export const GOVERNANCE_FINDINGS: Record<string, GovernanceFinding[]> = {
  "proj-storefront": [
    {
      id: "gf-storefront-1",
      projectId: "proj-storefront",
      serviceId: "svc-storefront-web",
      targetEnvClass: "production",
      governanceCommit: "a1b2c3d4e5f6a1b2c3d4e5f6",
      ruleId: "no-self-merge",
      detail: "The promoted change was self-merged with no second reviewer.",
      severity: "warning",
      status: "open",
      producedBy: "sys_governor",
      producedAt: daysAgo(1),
    },
    {
      id: "gf-storefront-2",
      projectId: "proj-storefront",
      serviceId: "svc-storefront-web",
      targetEnvClass: "production",
      governanceCommit: "a1b2c3d4e5f6a1b2c3d4e5f6",
      ruleId: "migration-review",
      detail: "A database migration shipped without a rollback plan.",
      severity: "info",
      status: "resolved",
      producedBy: "sys_governor",
      producedAt: daysAgo(3),
      resolvedBy: "admin",
      resolvedAt: daysAgo(2),
      resolutionNote: "Reviewed; the migration is additive and reversible.",
    },
  ],
};

// GOVERNANCE_POLICIES (ADR-0058 introspect, #571): the effective declared promotion policy per
// project, keyed by projectId — backs the web "advisory vs enforced" banner truthfully. Three
// scenarios, mirroring the server's own [IntrospectPolicyHandler] outcomes, so the banner's
// enforced/advisory/policy-error rendering all get exercised against realistic data (A-10, no
// surface can claim a state it never renders):
//   - proj-storefront:     declares NO promotion predicate — default-off, findings stay advisory.
//   - proj-internal-tools: declares `promote.require.max_open_findings` — findings are ENFORCED.
//   - proj-loyalty-app:    declares governance that is unparseable — every promote is denied
//                          fail-closed regardless of findings; the banner must say so, not guess.
// A project with no entry here (there are none today, but a fourth could be added) reads as
// default-off too, matching the server's own zero-value GovernancePolicy.
export const GOVERNANCE_POLICIES: Record<string, GovernancePolicy> = {
  "proj-storefront": {
    governanceConfigured: false,
    predicates: [],
  },
  "proj-internal-tools": {
    governanceConfigured: true,
    governanceCommit: "b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3",
    predicates: [
      { id: "promote.require.source_class", values: ["staging"] },
      { id: "promote.require.max_open_findings", values: ["2"] },
    ],
  },
  "proj-loyalty-app": {
    governanceConfigured: true,
    governanceCommit: "c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4",
    predicates: [],
    policyError: "governance denied (ambiguous promote.require.max_open_findings: declared twice)",
  },
};

export const SERVICES: Service[] = [
  {
    id: "svc-storefront-web",
    projectId: "proj-storefront",
    tenant: "fleet-prod",
    name: "web",
    image: "ghcr.io/acme/storefront-web:1.4.0",
    env: { NODE_ENV: "production" },
    secretRefs: ["storefront-db-url"],
    desiredCount: 2,
    status: "created",
    createdBy: "usr-admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "svc-storefront-worker",
    projectId: "proj-storefront",
    tenant: "fleet-prod",
    name: "worker",
    image: "ghcr.io/acme/storefront-worker:1.4.0",
    env: {},
    secretRefs: ["storefront-db-url"],
    desiredCount: 1,
    status: "created",
    createdBy: "usr-admin",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(5),
  },
  {
    id: "svc-storefront-api",
    projectId: "proj-storefront",
    tenant: "fleet-prod",
    name: "api",
    image: "ghcr.io/acme/storefront-api:2.4.1",
    env: { NODE_ENV: "production" },
    secretRefs: ["storefront-db-url"],
    desiredCount: 1,
    status: "created",
    createdBy: "usr-admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "svc-storefront-postgres",
    projectId: "proj-storefront",
    tenant: "fleet-prod",
    name: "postgres",
    image: "postgres:16",
    env: { POSTGRES_DB: "storefront" },
    secretRefs: ["storefront-db-password"],
    desiredCount: 1,
    status: "created",
    createdBy: "usr-admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(20),
  },
];

/*
 * ---- The Placement Graph (ADR-0025) ----
 * A small, realistic graph over proj-storefront: web -> api -> postgres,
 * spanning the project's two prod nodes (web/api co-located on
 * node-prod-web-1, postgres isolated on node-prod-db-1) — enough to exercise
 * a real multi-node topology in the graph view without inventing a third
 * project. web/api/postgres's placements all start "running" so the fixture
 * mostly reads as an already-deployed, steady-state fleet; worker's single
 * placement is deliberately left "pending" (never deployed) so mockClient's
 * `deployService` has one real pending -> deploying -> running arc to march
 * through on demand, matching what a freshly-created placement looks like
 * before its first deploy (see mockClient.ts's createPlacement).
 */

export const PLACEMENTS: Placement[] = [
  {
    id: "plc-storefront-web-1",
    serviceId: "svc-storefront-web",
    nodeId: "node-prod-web-1",
    status: "running",
    adopted: false,
    envOverrides: {},
    coLocate: "storefront-edge",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "plc-storefront-api-1",
    serviceId: "svc-storefront-api",
    nodeId: "node-prod-web-1",
    status: "running",
    adopted: false,
    envOverrides: {},
    coLocate: "storefront-edge",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2),
  },
  {
    id: "plc-storefront-postgres-1",
    serviceId: "svc-storefront-postgres",
    nodeId: "node-prod-db-1",
    status: "running",
    adopted: false,
    envOverrides: { PGDATA: "/var/lib/postgresql/data/pgdata" },
    coLocate: "",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(20),
  },
  {
    id: "plc-storefront-worker-1",
    serviceId: "svc-storefront-worker",
    nodeId: "node-prod-db-1",
    status: "pending",
    adopted: false,
    envOverrides: {},
    coLocate: "",
    createdAt: daysAgo(5),
    updatedAt: daysAgo(5),
  },
];

export const CONNECTIONS: Connection[] = [
  {
    id: "conn-storefront-web-api",
    fromService: "svc-storefront-web",
    toService: "svc-storefront-api",
    port: 3000,
    required: true,
    createdAt: daysAgo(20),
  },
  {
    id: "conn-storefront-api-postgres",
    fromService: "svc-storefront-api",
    toService: "svc-storefront-postgres",
    port: 5432,
    required: true,
    createdAt: daysAgo(20),
  },
];

// Fake-but-well-formed sha256 digests (64 hex chars) for the deployment
// timeline fixtures below — each is one repeated hex pair so its shape is
// obviously deterministic/synthetic (not meant to look like a real registry
// digest), while still exercising the real "sha256:<64 hex>" wire shape.
const DIGEST_241 = `sha256:${"a1".repeat(32)}`; // svc-storefront-api's current running version
const DIGEST_238 = `sha256:${"b2".repeat(32)}`; // the good version an auto-rollback restored
const DIGEST_242_BROKEN = `sha256:${"c3".repeat(32)}`; // resolved fine; failed its health gate
const DIGEST_239_REGRESSION = `sha256:${"d4".repeat(32)}`; // the bad version that triggered that auto-rollback

/*
 * ---- Deployment timeline (Host/Deploy UX Phase 2, #539) ----
 * DEPLOYMENTS is otherwise EMPTY for the same reason it always has been — a
 * deploy attempt is a real operator action (mockClient's `deployService`/
 * `rollbackDeployment`/`redeployDeployment`), never seeded wholesale — except
 * for svc-storefront-api, which carries a small, deliberately varied history
 * (oldest first below; the mock re-sorts newest-first on read) so the
 * timeline UI has real, believable rows to render out of the box: an
 * UNPINNED first deploy (from before digest capture existed), a healthy run,
 * a FAILED attempt, an auto ROLLED_BACK attempt, and the current DEPLOYING
 * (still converging) attempt — covering every state the timeline renders.
 * svc-storefront-api's one placement (plc-storefront-api-1, on
 * node-prod-web-1) sits in env-production, which `requiresPromotion: true`
 * (see ENVIRONMENTS above) — chosen on purpose so "Redeploy this version" on
 * this service demonstrates the real requires_promotion refusal, while
 * "Roll back to this" on the SAME service demonstrates that rollback is NOT
 * subject to that gate (ADR-0031 §2b) — both from real fixture data, not a
 * special-cased hook.
 */
export const DEPLOYMENTS: Deployment[] = [
  {
    // The first-ever deploy of this service, predating per-deploy digest
    // capture (#539): image/tag are known, digest is honestly absent —
    // "unpinned" — never a fabricated value (A-10).
    id: "dep-storefront-api-1",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "running",
    targetCount: 1,
    convergedCount: 1,
    image: "ghcr.io/acme/storefront-api:2.0.0",
    tag: "2.0.0",
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    createdBy: "admin",
    createdAt: daysAgo(20),
    updatedAt: daysAgo(20),
  },
  {
    // A healthy, fully-pinned deploy — eligible for "Roll back to this" /
    // "Redeploy this version" (status "running", a recorded image).
    id: "dep-storefront-api-2",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "running",
    targetCount: 1,
    convergedCount: 1,
    image: "ghcr.io/acme/storefront-api:2.3.8",
    tag: "2.3.8",
    digest: DIGEST_238,
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    previousImage: "ghcr.io/acme/storefront-api:2.0.0",
    createdBy: "admin",
    createdAt: daysAgo(14),
    updatedAt: daysAgo(14),
  },
  {
    // A regression that halted mid-rollout and was auto-rolled-back (ADR-0031
    // §2b) — the placement that already cycled was restored to 2.3.8, so this
    // reads as "rolled_back", not "failed": the fleet came back to a known-good
    // state on its own.
    id: "dep-storefront-api-3",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "rolled_back",
    targetCount: 1,
    convergedCount: 1,
    image: "ghcr.io/acme/storefront-api:2.3.9-regression",
    tag: "2.3.9-regression",
    digest: DIGEST_239_REGRESSION,
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    previousImage: "ghcr.io/acme/storefront-api:2.3.8",
    createdBy: "operator",
    createdAt: daysAgo(9),
    updatedAt: daysAgo(9),
  },
  {
    // Pulled fine (digest resolved) but failed its health gate — a genuine
    // failure, distinct from the rolled_back row above: nothing here reached
    // "running", so convergedCount stays 0 of target.
    id: "dep-storefront-api-4",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "failed",
    targetCount: 1,
    convergedCount: 0,
    detail: "placement plc-storefront-api-1: container failed health check after 3 attempts",
    image: "ghcr.io/acme/storefront-api:2.4.2-broken",
    tag: "2.4.2-broken",
    digest: DIGEST_242_BROKEN,
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    previousImage: "ghcr.io/acme/storefront-api:2.3.8",
    createdBy: "operator",
    createdAt: daysAgo(4),
    updatedAt: daysAgo(4),
  },
  {
    // The current, fixed-forward version (svc-storefront-api's `image` in
    // SERVICES above matches this) — healthy and the newest terminal entry.
    id: "dep-storefront-api-5",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "running",
    targetCount: 1,
    convergedCount: 1,
    image: "ghcr.io/acme/storefront-api:2.4.1",
    tag: "2.4.1",
    digest: DIGEST_241,
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    previousImage: "ghcr.io/acme/storefront-api:2.3.8",
    createdBy: "admin",
    createdAt: daysAgo(2),
    updatedAt: daysAgo(2),
  },
  {
    // A fresh redeploy of the same content, still converging — the newest
    // row, demonstrating the timeline's LIVE state: targetCount 1,
    // convergedCount 0 until the (mocked) rollout finishes. Digest is already
    // known (redeploy pins to it up front) even though the row hasn't
    // converged yet — an honest "known artifact, not yet converged" state,
    // distinct from the unpinned row above ("never resolved any digest").
    id: "dep-storefront-api-6",
    serviceId: "svc-storefront-api",
    tenant: "fleet-prod",
    status: "deploying",
    targetCount: 1,
    convergedCount: 0,
    image: "ghcr.io/acme/storefront-api:2.4.1",
    tag: "2.4.1",
    digest: DIGEST_241,
    strategy: "rolling",
    maxUnavailable: 1,
    haltOnly: false,
    previousImage: "ghcr.io/acme/storefront-api:2.3.8",
    createdBy: "admin",
    createdAt: minutesAgo(2),
    updatedAt: minutesAgo(2),
  },
];

/**
 * Per-placement convergence breakdown for the DEPLOYMENTS above, keyed by
 * deployment id — the fixture twin of what `summarizeOutcomes` writes
 * server-side (server/internal/projectsvc/deploy.go), read back by
 * `getDeployment`'s detail view. Mirrors `DeploymentPlacementOutcome`
 * exactly. `dep-storefront-api-6` (still "deploying") is deliberately ABSENT
 * from this map — mockClient's `getDeployment` falls back to an empty
 * `placements` array for any id with no entry here, matching the server's own
 * honest "no outcome summary yet" behavior for an in-flight deploy (A-10):
 * the timeline's `convergedCount`/`targetCount` is the correct signal to show
 * while this is empty, not a fabricated per-placement row.
 */
export const DEPLOYMENT_PLACEMENTS: Record<string, DeploymentPlacementOutcome[]> = {
  "dep-storefront-api-1": [
    { placementId: "plc-storefront-api-1", nodeId: "node-prod-web-1", status: "running" },
  ],
  "dep-storefront-api-2": [
    { placementId: "plc-storefront-api-1", nodeId: "node-prod-web-1", status: "running", digest: DIGEST_238 },
  ],
  "dep-storefront-api-3": [
    {
      placementId: "plc-storefront-api-1",
      nodeId: "node-prod-web-1",
      status: "rolled_back",
      digest: DIGEST_238,
    },
  ],
  "dep-storefront-api-4": [
    {
      placementId: "plc-storefront-api-1",
      nodeId: "node-prod-web-1",
      status: "failed",
      digest: DIGEST_242_BROKEN,
      error: "container failed health check after 3 attempts: connection refused on :8080",
    },
  ],
  "dep-storefront-api-5": [
    { placementId: "plc-storefront-api-1", nodeId: "node-prod-web-1", status: "running", digest: DIGEST_241 },
  ],
};

/*
 * ---- Service domain bindings (ADR-0026 §3/§5) ----
 * One bound hostname on web (the fixture's public-facing service) so the
 * Domains section has something real to render, plus a second on api to
 * exercise more than one row/service. Both hostnames are already lowercase —
 * the "operator typed mixed case, server canonicalized it" path is exercised
 * live via createDomain, not faked in a fixture. Each carries a node-reported
 * `status` (#303): shop is `active` (serving), api is `pending` (its cert is
 * still issuing) — the two states the UI most needs to distinguish.
 */
export const DOMAINS: Domain[] = [
  {
    id: "dom-storefront-web-1",
    serviceId: "svc-storefront-web",
    hostname: "shop.acme.example",
    targetPort: 3000,
    createdAt: daysAgo(18),
    status: "active",
  },
  {
    id: "dom-storefront-api-1",
    serviceId: "svc-storefront-api",
    hostname: "api.acme.example",
    targetPort: 8080,
    createdAt: daysAgo(10),
    status: "pending",
  },
];

/*
 * ---- Fleet health matrix (ADR-0030 P4-2) ----
 * No fixture array here on purpose: a health cell is a DERIVED VIEW ONLY
 * (charter A-5, see HealthCell's doc comment in types.ts), so mockClient.ts
 * computes it live from the working PLACEMENTS/SERVICES/NODES maps on every
 * `listHealthMatrix` call — the same "never a second copy that can drift"
 * posture the real server's `HealthMatrix` follows. Baking a static fixture
 * here would let it silently disagree with a placement created/edited
 * through this mock at runtime.
 */

/*
 * ---- Fleet-health alerting (ADR-0030 P4-5) ----
 * All scoped to fleet-prod (mockClient.ts's `alertRuleFleet` map — see its
 * doc comment for why that association isn't on `AlertRule` itself). Three
 * rules exercise the CRUD surface's real variety: an enabled rule with a
 * webhook and a live firing instance (node offline — node-prod-edge-1 is
 * already "offline" in NODES above, so this reads as a believable, already
 * -alerting fleet); an enabled rule with no webhook configured and one
 * resolved instance; a disabled, project-scoped rule with no instances yet.
 */
export const ALERT_RULES: AlertRule[] = [
  {
    id: "alr-node-offline",
    name: "Node offline",
    condition: "node_offline",
    threshold: 300,
    webhookUrl: "https://hooks.acme.example/comebye-alerts",
    enabled: true,
    createdAt: daysAgo(14),
    updatedAt: daysAgo(2),
  },
  {
    id: "alr-placement-failed",
    name: "Any placement fails",
    condition: "placement_failed",
    threshold: 0,
    webhookUrl: "",
    enabled: true,
    createdAt: daysAgo(14),
    updatedAt: daysAgo(14),
  },
  {
    id: "alr-cert-expiry",
    name: "Storefront cert expiring soon",
    condition: "cert_expiry",
    threshold: 14,
    scope: "proj-storefront",
    webhookUrl: "https://hooks.acme.example/cert-alerts",
    enabled: false,
    createdAt: daysAgo(9),
    updatedAt: daysAgo(1),
  },
];

export const ALERT_INSTANCES: Record<string, AlertInstance[]> = {
  "alr-node-offline": [
    {
      id: "ali-edge-1-offline",
      ruleId: "alr-node-offline",
      state: "firing",
      resourceId: "node-prod-edge-1",
      firstSeenAt: hoursAgo(6),
      lastSeenAt: minutesAgo(2),
    },
  ],
  "alr-placement-failed": [
    {
      id: "ali-worker-resolved",
      ruleId: "alr-placement-failed",
      state: "resolved",
      resourceId: "plc-storefront-worker-1",
      firstSeenAt: daysAgo(3),
      lastSeenAt: daysAgo(3),
      resolvedAt: daysAgo(3),
    },
  ],
  "alr-cert-expiry": [],
};
